import { useId } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { academicStructureApi } from "@/lib/api/academicStructure";
import { CLASSROOM_INTAKE_QUERY_KEY, classroomIntakeApi, type ClassroomIntakePreview } from "@/lib/api/classroomIntake";
import { primaryOptions, rowName, type Choices, type SectionDraft, type StudentPlacement } from "./intake-plan";



export function SectionBlock({
  preview,
  section,
  onChange,
}: {
  preview: ClassroomIntakePreview;
  section: SectionDraft;
  onChange: (section: SectionDraft) => void;
}) {
  const id = useId();
  const semesters = useQuery({ queryKey: ["semesters"], queryFn: academicStructureApi.getSemesters });
  const sections = useQuery({
    queryKey: [...CLASSROOM_INTAKE_QUERY_KEY, "linkable-sections"],
    queryFn: () => classroomIntakeApi.linkableSections(),
    enabled: section.mode === "existing_section",
  });
  const terms = (semesters.data ?? []).filter((term) => term.status !== "closed");
  const linkable = sections.data?.sections ?? [];

  return (
    <section aria-labelledby={`${id}-heading`}>
      <div className="grid gap-1">
        <h3 id={`${id}-heading`} className="text-section font-semibold text-foreground">Section</h3>
        <p className="text-ui text-muted-foreground">The IMAM section this course becomes. A new section starts as a draft.</p>
      </div>
      <div className="grid gap-4">
        <RadioGroup
          aria-label="Section"
          value={section.mode}
          onValueChange={(value) => onChange({ ...section, mode: value as SectionDraft["mode"] })}
          className="flex flex-wrap gap-x-5 gap-y-1"
        >
          {([["new_draft", "New draft section"], ["existing_section", "Link an existing section"]] as const).map(([value, label]) => (
            <Label key={value} htmlFor={`${id}-${value}`} className="flex min-h-11 cursor-pointer items-center gap-1 pe-2 text-ui font-normal text-foreground">
              <RadioGroupItem id={`${id}-${value}`} value={value} />
              {label}
            </Label>
          ))}
        </RadioGroup>

        {section.mode === "new_draft" ? (
          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            <div className="grid min-w-0 gap-1.5">
              <Label htmlFor={`${id}-code`}>Section code</Label>
              <Input
                id={`${id}-code`}
                required
                aria-describedby={`${id}-code-hint`}
                autoComplete="off"
                className="font-mono"
                value={section.code}
                onChange={(event) => onChange({ ...section, code: event.target.value })}
              />
              <p id={`${id}-code-hint`} className="text-meta text-muted-foreground">Type the IMAM code. It is never read from the Classroom name.</p>
            </div>
            <div className="grid min-w-0 gap-1.5">
              <Label htmlFor={`${id}-title`}>Section title</Label>
              <Input
                id={`${id}-title`}
                required
                value={section.title ?? preview.proposed_section.title}
                onChange={(event) => onChange({ ...section, title: event.target.value })}
              />
            </div>
            <div className="grid min-w-0 gap-1.5 sm:col-span-2 sm:max-w-sm">
              <Label htmlFor={`${id}-term`}>Term</Label>
              <NativeSelect
                id={`${id}-term`}
                required
                value={section.semesterId}
                disabled={semesters.isLoading}
                onChange={(event) => onChange({ ...section, semesterId: event.target.value })}
              >
                <option value="">{semesters.isLoading ? "Loading terms" : "Choose a term"}</option>
                {terms.map((term) => <option key={term.id} value={term.id}>{term.name}</option>)}
              </NativeSelect>
              {semesters.isError && <InlineRetry what="Terms" onRetry={() => void semesters.refetch()} />}
              {semesters.isSuccess && terms.length === 0 && (
                <p className="text-meta text-muted-foreground">No term is open. Add one in Academic Structure first.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid min-w-0 gap-1.5 sm:max-w-xl">
            <Label htmlFor={`${id}-existing`}>Section to link</Label>
            <NativeSelect
              id={`${id}-existing`}
              aria-describedby={`${id}-existing-hint`}
              value={section.courseId}
              disabled={sections.isLoading}
              onChange={(event) => onChange({ ...section, courseId: event.target.value })}
            >
              <option value="">{sections.isLoading ? "Loading sections" : "Choose a section"}</option>
              {linkable.map((candidate) => (
                <option key={candidate.course_id} value={candidate.course_id}>
                  {[candidate.code, candidate.title, candidate.term_name].filter(Boolean).join(" · ")}
                </option>
              ))}
            </NativeSelect>
            <p id={`${id}-existing-hint`} className="text-meta text-muted-foreground">
              Sections in a closed term, archived sections, and sections already linked to a Classroom course are not listed. Students already enrolled are marked below.
            </p>
            {sections.isError && <InlineRetry what="Sections" onRetry={() => void sections.refetch()} />}
            {sections.isSuccess && linkable.length === 0 && (
              <p className="text-meta text-muted-foreground">No section can be linked. Create a new draft section instead.</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}



export function InlineRetry({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-meta">
      <p className="text-destructive">{what} could not be loaded.</p>
      <Button type="button" size="sm" variant="outline" onClick={onRetry}>Try again</Button>
    </div>
  );
}



export function PrimaryChooser({
  preview,
  choices,
  primary,
  onChange,
}: {
  preview: ClassroomIntakePreview;
  choices: Choices;
  primary: string | null;
  onChange: (providerUserId: string | null) => void;
}) {
  const id = useId();
  const options = primaryOptions(preview, choices);
  const owner = preview.teachers.find((row) => row.provider_user_id === preview.proposed_primary_provider_user_id);
  return (
    <div className="grid max-w-xl gap-1.5">
      <Label htmlFor={`${id}-primary`}>Primary instructor</Label>
      <NativeSelect
        id={`${id}-primary`}
        aria-describedby={`${id}-primary-hint`}
        value={primary ?? ""}
        onChange={(event) => onChange(event.target.value || null)}
      >
        <option value="">Choose the primary instructor</option>
        {options.map((row) => (
          <option key={row.provider_user_id} value={row.provider_user_id}>
            {rowName(row)}{row.is_owner ? " (Classroom course owner)" : ""}
          </option>
        ))}
      </NativeSelect>
      <p id={`${id}-primary-hint`} className="text-meta text-muted-foreground">
        {owner
          ? `Proposed: ${rowName(owner)}, the Classroom course owner. Every other included teacher joins as a co-instructor.`
          : "The Classroom course owner is not one of its teachers, so no primary instructor is proposed. Every other included teacher joins as a co-instructor."}
      </p>
    </div>
  );
}



export function NewStudentPlacement({
  placement,
  onChange,
}: {
  placement: StudentPlacement;
  onChange: (placement: StudentPlacement) => void;
}) {
  const id = useId();
  const programs = useQuery({ queryKey: ["programs"], queryFn: academicStructureApi.getPrograms });
  const cohorts = useQuery({
    queryKey: ["cohorts", "active"],
    queryFn: () => academicStructureApi.getCohorts({ status: "active" }),
  });
  const programOptions = (programs.data ?? []).filter((program) => program.status === "active" && program.code);
  const program = programOptions.find((candidate) => candidate.code === placement.programKey);
  const cohortOptions = (cohorts.data ?? []).filter((cohort) => !program || !cohort.program_id || cohort.program_id === program.id);

  return (
    <fieldset className="grid min-w-0 gap-4">
      <legend className="mb-1 text-ui font-semibold text-foreground">New students</legend>
      <p className="-mt-2 text-meta text-muted-foreground">
        Applies to every student created here, and to each linked person who holds no student record yet. A student ID&apos;s year comes from the cohort, or from the admitted year when no cohort is chosen.
      </p>
      <div className="grid min-w-0 gap-4 sm:grid-cols-3">
        <div className="grid min-w-0 gap-1.5">
          <Label htmlFor={`${id}-program`}>Program (optional)</Label>
          <NativeSelect
            id={`${id}-program`}
            value={placement.programKey}
            onChange={(event) => {
              const programKey = event.target.value;
              const chosen = programOptions.find((candidate) => candidate.code === programKey);
              const cohort = (cohorts.data ?? []).find((candidate) => candidate.name === placement.cohortKey);
              const keepsCohort = !chosen || !cohort?.program_id || cohort.program_id === chosen.id;
              onChange(keepsCohort ? { ...placement, programKey } : { ...placement, programKey, cohortKey: "", cohortYear: null });
            }}
          >
            <option value="">No program</option>
            {programOptions.map((candidate) => (
              <option key={candidate.id} value={candidate.code!}>{candidate.name} ({candidate.code})</option>
            ))}
          </NativeSelect>
          {programs.isError && <InlineRetry what="Programs" onRetry={() => void programs.refetch()} />}
        </div>
        <div className="grid min-w-0 gap-1.5">
          <Label htmlFor={`${id}-cohort`}>Cohort (optional)</Label>
          <NativeSelect
            id={`${id}-cohort`}
            value={placement.cohortKey}
            onChange={(event) => {
              const cohort = cohortOptions.find((candidate) => candidate.name === event.target.value);
              onChange({ ...placement, cohortKey: cohort?.name ?? "", cohortYear: cohort?.entry_year ?? null });
            }}
          >
            <option value="">No cohort</option>
            {cohortOptions.map((cohort) => (
              <option key={cohort.id} value={cohort.name}>{cohort.name} · entry {cohort.entry_year}</option>
            ))}
          </NativeSelect>
          {cohorts.isError && <InlineRetry what="Cohorts" onRetry={() => void cohorts.refetch()} />}
        </div>
        <div className="grid min-w-0 gap-1.5">
          <Label htmlFor={`${id}-year`}>Admitted year</Label>
          <Input
            id={`${id}-year`}
            inputMode="numeric"
            autoComplete="off"
            className="tabular-nums"
            disabled={!!placement.cohortKey}
            value={placement.cohortKey ? String(placement.cohortYear ?? "") : placement.admittedYear}
            onChange={(event) => onChange({ ...placement, admittedYear: event.target.value })}
          />
        </div>
      </div>
    </fieldset>
  );
}